import { Component, Input, inject } from '@angular/core';
import {
  BlockType,
  Booking,
  BookingStatus,
} from '../../../core/models/entities/booking';
import { Room } from '../../../core/models/entities/room';
import { differenceInHours, format, parseISO } from 'date-fns';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-booking-calendar-block',
  template: `
    <div
      class="relative my-auto mt-1 flex h-12 flex-col justify-center border"
      [class.bg-light]="
        booking.status === BookingStatus.Pending ||
        booking.status === BookingStatus.PendingPayment
      "
      data-cy="booking"
      [attr.data-status]="booking.status"
      [ngClass]="{
        'bg-white':
          room.active &&
          !booking.is_public &&
          booking.block_type === BlockType.Booking,
        'bg-light': !room.active,
        'bg-gray-50': room.active && booking.block_type === BlockType.Option,
        'bg-primary-50':
          room.active &&
          booking.is_public &&
          booking.block_type === BlockType.Booking,
        'border-gray-muted text-gray-muted':
          !room.active ||
          booking.status === BookingStatus.Pending ||
          booking.status === BookingStatus.PendingPayment,
        'bg-disabled': isBlocked,
        'border-secondary-900':
          room.active &&
          booking.status !== BookingStatus.Pending &&
          booking.status !== BookingStatus.PendingPayment,
        'cursor-pointer': room.active && isBookingOrOption,
        'z-0': !isBookingOrOption,
        'z-10': isBookingOrOption,
        'border-l-0': overflowsStart,
        'border-r-0': overflowsEnd,
        'rounded-l-md': !overflowsStart,
        'rounded-r-md': !overflowsEnd,
      }"
      >
      <div class="flex justify-between overflow-hidden">
        <div data-cy="booking-name" class="truncate text-sm font-medium">
          <span class="mx-2" [class.text-gray-muted]="!isBookingOrOption">
            @if (isBookingOrOption) {
              {{ name }}
            } @else if (isBlocked) {
              {{ blockedLabel }}
            }
          </span>
        </div>
      </div>
      <div
        data-cy="booking-start-end"
        class="mx-2 truncate text-sm"
        [class.text-gray-muted]="isBlocked"
        >{{ startEndText
        }}@if (endsNextDay) {
        <span
          class="pl-2 text-gray-500"
          [class.text-gray-muted]="!isBookingOrOption"
          >+1</span
          >
          }</div
          >
          @if (
            booking.status === BookingStatus.Pending &&
            booking.block_type === BlockType.Booking
            ) {
            <div
              class="absolute top-0 right-0 h-4 w-4 rounded-full bg-pink-600"
              [style.transform]="'translate(0.5rem, -0.5rem)'"
            ></div>
          }
        </div>
    `,
  standalone: false,
})
export class BookingCalendarBlockComponent {
  private translocoService = inject(TranslocoService);

  @Input() room: Room;
  @Input() booking: Booking;
  @Input() name: string;
  @Input({ required: true }) start: string;
  @Input({ required: true }) end: string;
  @Input({ required: true }) endsNextDay: boolean;
  @Input({ required: true }) overflowsEnd: boolean;
  @Input({ required: true }) overflowsStart: boolean;

  // Appelé depuis le template : évalué après le chargement des traductions.
  protected get blockedLabel(): string {
    return this.translocoService.translate('bookings.calendar-block.blocked');
  }

  get startEndText() {
    const start = parseISO(this.start);
    const end = parseISO(this.end);
    return differenceInHours(end, start) <= 24
      ? format(start, 'HH:mm') + ' - ' + format(end, 'kk:mm')
      : format(
          start,
          this.translocoService.translate('common.date-format.datetime-numeric'),
        ) +
          ' - ' +
          format(
            end,
            this.translocoService.translate(
              'common.date-format.datetime-numeric-short',
            ),
          );
  }

  get isBookingOrOption() {
    return (
      this.booking.block_type === BlockType.Booking ||
      this.booking.block_type === BlockType.Option
    );
  }

  get isBlocked() {
    return (
      this.booking.block_type === BlockType.RoomBlocked ||
      this.booking.block_type === BlockType.RoomWithoutControl
    );
  }

  protected readonly BookingStatus = BookingStatus;
  protected readonly BlockType = BlockType;
}
